import { useState, useCallback, useEffect } from 'react';
import { Modal, Box, Button, Typography, CircularProgress, Select, MenuItem } from '@mui/material';

import {
    Loading,
    ErrorMessage,
    DataTable,
    type TableColumn,
    PreviewIcon,
    CheckIcon,
    XIcon,
    Label,
} from '@/src/ui';
import type {
    StudentExamAttemptFullDTO,
    StudentExamAttemptQuestionDTO,
} from '../models/studentHistoryDTOts';
import {
    Constants,
    ReportHeaderStyle,
    ReportCellStyle,
    utcToLocal,
    formatOptions,
    type Option,
} from '@/src/utils';
import { apiRequest } from '@/src/services';
import PreviewQuestionModal from './PreviewQuestionModal';

interface StudentAttemptsModalProps {
    studentId: number;
    examId: number;
    attemptNumber: number;
    maxAttempt: number;
    open: boolean;
    onClose: () => void;
}

export default function StudentAttemptsModal({
    studentId,
    examId,
    attemptNumber,
    maxAttempt,
    open,
    onClose,
}: StudentAttemptsModalProps) {
    const [previewQuestionData, setPreviewQuestionData] = useState<{
        questionId: number;
        submittedAnswer: string;
        questionAnswer: string | null;
    } | null>(null);

    const [loadingQuestionId, setLoadingQuestionId] = useState<number | null>(null);
    const [data, setData] = useState<StudentExamAttemptFullDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [selectedAttemptNumber, setSelectedAttemptNumber] = useState(attemptNumber);

    const attemptOptions: Option[] = Array.from({ length: maxAttempt }, (_, i) => ({
        id: i + 1,
        name: `Attempt ${i + 1}`,
    }));

    const refreshTableData = useCallback(async () => {
        setError('');
        setLoading(true);

        try {
            const res = await apiRequest<StudentExamAttemptFullDTO>(
                'GET',
                `reports/studentHistoryReport/singleAttempt?studentId=${studentId}&attemptNumber=${selectedAttemptNumber}&examId=${examId}`
            );
            setData(res.data);
        } catch {
            setError('Failed to load Student History Report');
        } finally {
            setLoading(false);
        }
    }, [studentId, selectedAttemptNumber, examId]);

    useEffect(() => {
        setData(null);
        refreshTableData();
    }, [studentId, selectedAttemptNumber, examId, refreshTableData]);

    // Sort questions by displayOrder ascending
    const questions = [...(data?.studentExamAttemptQuestions ?? [])].sort(
        (a, b) => a.displayOrder - b.displayOrder
    );

    // Define table columns
    const columns: TableColumn[] = [
        {
            dataKey: 'displayOrder',
            header: '#',
            align: 'center',
            allowSort: true,
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'questionType',
            header: 'Question Type',
            align: 'left',
            allowSort: true,
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'standards',
            header: 'Standards',
            align: 'left',
            formatter: (row: StudentExamAttemptQuestionDTO) =>
                Array.isArray(row.standards) ? row.standards.join(', ') : '—',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'isCorrect',
            header: 'Correct',
            align: 'center',
            formatter: (row: StudentExamAttemptQuestionDTO) => {
                if (row.questionType === 'Stimulus') {
                    return null;
                }
                return (
                    <Box
                        className="icon-button"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        {row.isCorrect ? (
                            <CheckIcon color="green" style={{ width: '28px', height: '28px' }} />
                        ) : (
                            <XIcon color="red" style={{ width: '28px', height: '28px' }} />
                        )}
                    </Box>
                );
            },
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'scoredBy',
            header: 'Scored By',
            align: 'left',
            formatter: (row: StudentExamAttemptQuestionDTO) => {
                if (row.questionType === 'Stimulus') {
                    return null;
                }
                if (!row.isHandScore) return 'N/A';
                else if (!row.scoredBy) return <div className="text-red-500">'Incomplete'</div>;
                else return row.scoredBy;
            },
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'scoredDTUTC',
            header: 'Scored At',
            align: 'left',
            formatter: (row: StudentExamAttemptQuestionDTO) => {
                if (row.questionType === 'Stimulus') {
                    return null;
                }
                if (!row.scoredDTUTC) return '-';
                return utcToLocal(row.scoredDTUTC).toLocaleString(undefined, formatOptions);
            },
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'viewQuestion',
            header: 'View Question',
            align: 'center',
            formatter: (row: StudentExamAttemptQuestionDTO) => (
                <Button
                    size="small"
                    className="icon-button"
                    startIcon={<PreviewIcon style={{ width: '28px', height: '28px' }} />}
                    disabled={loadingQuestionId === row.id}
                    onClick={async () => {
                        if (previewQuestionData?.questionId === row.questionId) return;

                        setLoadingQuestionId(row.id);

                        // Open side panel immediately with loader
                        setPreviewQuestionData({
                            questionId: row.questionId,
                            submittedAnswer: row.submittedAnswer,
                            questionAnswer: null,
                        });

                        try {
                            const result = await apiRequest(
                                'get',
                                `/assessments/studentExamAttemptQuestions/viewWithAnswer/${row.id}`
                            );

                            setPreviewQuestionData({
                                questionId: row.questionId,
                                submittedAnswer: row.submittedAnswer,
                                questionAnswer: (result.data as { answer: string }).answer,
                            });
                        } catch (error) {
                            console.error('Failed to load question preview:', error);
                            setPreviewQuestionData({
                                questionId: row.questionId,
                                submittedAnswer: row.submittedAnswer,
                                questionAnswer: 'Error loading question',
                            });
                        } finally {
                            setLoadingQuestionId(null);
                        }
                    }}
                ></Button>
            ),
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
    ];

    return (
        <Modal
            open={open}
            onClose={() => {
                setPreviewQuestionData(null);
                onClose();
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 0,
                    width: '85%',
                    height: '85%',
                    display: 'flex',
                    overflow: 'hidden',
                }}
            >
                <Loading visible={loading} message="Loading report data..." />
                <ErrorMessage error={error} />
                {data ? (
                    <>
                        {/* LEFT: Questions Table */}
                        <Box
                            sx={{
                                width: previewQuestionData ? '60%' : '100%',
                                flexShrink: 0,
                                p: 3,
                                overflowY: 'auto',
                                transition: 'width 0.5s ease',
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold" mb={2}>
                                Student: {data.studentName} <br />
                                Exam: {data.examName} (Attempt #{data.attemptNumber})
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 3,
                                }}
                            >
                                <Typography variant="body2">
                                    Reporting Category:{' '}
                                    <Label color={Constants.bandColors[data.reportingCategory]}>
                                        {data.reportingCategory ?? '—'}
                                    </Label>{' '}
                                    | Score:{' '}
                                    <strong>{data.score != null ? `${data.score}%` : '—'}</strong> |
                                    Started:{' '}
                                    <strong>
                                        {data.startDTUTC
                                            ? utcToLocal(data.startDTUTC).toLocaleString(
                                                  undefined,
                                                  formatOptions
                                              )
                                            : '—'}
                                    </strong>
                                </Typography>
                                <Select
                                    size="small"
                                    value={selectedAttemptNumber}
                                    onChange={(e) =>
                                        setSelectedAttemptNumber(Number(e.target.value))
                                    }
                                    sx={{
                                        minWidth: 120,
                                        fontSize: '0.875rem',
                                        height: 32,
                                        '& .MuiSelect-select': { py: 0.5 },
                                    }}
                                >
                                    {attemptOptions.map((a) => (
                                        <MenuItem key={a.id} value={a.id}>
                                            {a.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            {questions.length > 0 ? (
                                <DataTable
                                    columns={columns}
                                    data={questions}
                                    tableName={`Attempt #${data.attemptNumber}`}
                                    enableExportCSV
                                    alternatingRowColor
                                    defaultSortColumn="displayOrder"
                                    defaultSortDirection="asc"
                                />
                            ) : (
                                <Typography color="text.secondary">
                                    No questions available for this attempt.
                                </Typography>
                            )}

                            <Box textAlign="right" mt={3}>
                                <Button variant="outlined" onClick={onClose}>
                                    Close
                                </Button>
                            </Box>
                        </Box>

                        {/* RIGHT: Question Preview Panel */}
                        {previewQuestionData && (
                            <Box
                                sx={{
                                    width: '40%',
                                    flexShrink: 0,
                                    borderLeft: '1px solid #ddd',
                                    overflowY: 'auto',
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'opacity 0.5s ease',
                                }}
                            >
                                {previewQuestionData.questionAnswer === null ? (
                                    <CircularProgress />
                                ) : previewQuestionData.questionAnswer ===
                                  'Error loading question' ? (
                                    <Typography color="error">Error loading question</Typography>
                                ) : (
                                    <PreviewQuestionModal
                                        questionId={previewQuestionData.questionId}
                                        submittedAnswer={previewQuestionData.submittedAnswer}
                                        questionAnswer={previewQuestionData.questionAnswer}
                                        onClose={() => setPreviewQuestionData(null)}
                                    />
                                )}
                            </Box>
                        )}
                    </>
                ) : null}
            </Box>
        </Modal>
    );
}
